import AppContext from 'App/AppContext';
import { IPFontIcon, IPLabel, IPText } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPTextField } from 'baseComponents/TextField/IPTextField';
import { canUpdateTaskField, TaskFieldTypes } from 'components/Tasks/TaskAuthHelper';
import { globalStackTokensGapExtraSmall, globalStackTokensGapMedium } from 'globalStyles';
import Task from 'models/tasks/task';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGetTaskExternalId } from 'services/Api/taskService';
import { apiRequest } from 'services/Auth/authConfig';
import { canShowTaskExternalId } from './taskDetailRules';
import { useIsInTaskDetailCallout } from './TaskDetailRow';
import { iconFontSize, readOnlyRowSpace } from './TaskDetailStyles';

interface ITaskDetailExternalIdProps {
  orgTask: Task;
  task: Task;
  canUpdate: boolean;
  onUpdate: (newTask: Task) => void;
}

export const TaskDetailExternalId = (props: ITaskDetailExternalIdProps) => {
  const isInCallout = useIsInTaskDetailCallout();
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['task', 'translation']);
  const isReadonly = !canUpdateTaskField(props.orgTask, props.canUpdate, TaskFieldTypes.ExternalId, appContext);

  const validateExternalId = async (): Promise<string> => {
    const externalId = props.task.externalId?.trim() ?? '';
    if (!externalId || externalId === props.orgTask.externalId) {
      return '';
    }

    try {
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const taskId = await apiGetTaskExternalId(accessToken, externalId);
      if (taskId && taskId > 0 && taskId !== props.orgTask.taskId) {
        return t('task:ExternalId.Error');
      }
    } catch (err) {
      appContext.setError(err);
    }

    return '';
  };

  //
  // Main render
  //
  if (!canShowTaskExternalId(appContext)) {
    return null;
  }

  return (
    <IPStack
      horizontal
      tokens={appContext.isMobileView ? globalStackTokensGapExtraSmall : globalStackTokensGapMedium}
      verticalAlign="start"
      styles={isReadonly ? { root: { paddingBottom: readOnlyRowSpace } } : undefined}
    >
      {!isInCallout && (
        <IPStack.Item>
          {!appContext.isMobileView && <IPFontIcon iconName="Barcode" style={iconFontSize} />}
        </IPStack.Item>
      )}
      <IPStack.Item grow>
        {isReadonly && (
          <IPStack>
            <IPLabel>{t('task:ExternalId.Label')}</IPLabel>
            <IPText>{props.task.externalId || '-'}</IPText>
          </IPStack>
        )}
        {!isReadonly && (
          <IPTextField
            label={t('task:ExternalId.Label')}
            value={props.task.externalId ?? ''}
            placeholder={t('task:ExternalId.Placeholder')}
            onGetErrorMessage={validateExternalId}
            onChange={(event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
              const copyTask = props.task.clone();
              copyTask.externalId = newValue ? newValue : '';
              props.onUpdate(copyTask);
            }}
            maxLength={256}
          />
        )}
      </IPStack.Item>
    </IPStack>
  );
};
